import { Loader2, Image as ImageIcon, Upload, X } from "lucide-react"
import { Image } from "@city-sage/aurum-avis-labs-components"
import { Image as ImageType } from "@city-sage/core-client"
import { PreviewImage } from "./types/createStopTypes"

interface ImageGallerySectionProps {
  previewImages: PreviewImage[] | undefined
  imagesLoading: boolean
  selectedImageIndex: number
  uploadedImage: ImageType | null
  uploadedImagePreview: string | null
  isUsingUploadedImage: boolean
  isUploading: boolean
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
  onPreviewImageSelect: (index: number) => void
  onRemoveUploadedImage: () => void
}

export function ImageGallerySection({
  previewImages,
  imagesLoading,
  selectedImageIndex,
  uploadedImage,
  uploadedImagePreview,
  isUsingUploadedImage,
  isUploading,
  onImageUpload,
  onPreviewImageSelect,
  onRemoveUploadedImage
}: ImageGallerySectionProps) {
  
  // Helper function to convert PreviewImage to Image entity format for attribution
  const createImageEntity = (previewImage: PreviewImage) => {
    if (!previewImage.source || previewImage.source !== 'unsplash' || !previewImage.authorName) {
      return undefined;
    }
    
    return {
      id: previewImage.id,
      url: previewImage.url,
      previewUrl: previewImage.thumbnailUrl || null,
      thumbnailUrl: previewImage.thumbnailUrl || null,
      width: previewImage.width || null,
      height: previewImage.height || null,
      source: previewImage.source as any, // Convert to ImageSource enum
      attribution: previewImage.attribution || null,
      sourceUrl: null,
      authorName: previewImage.authorName || null,
      authorUrl: previewImage.authorUrl || null,
      description: previewImage.description || null,
      unsplashId: null,
      photographerUsername: null,
      downloadUrl: null,
      createdAt: previewImage.createdAt,
      updatedAt: previewImage.updatedAt,
    };
  };
  return (
    <div className="bg-white mt-2 p-4">
      <div className="space-y-4">
        {/* Image Gallery */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">Choose an image</p>
          </div>
          
          {imagesLoading ? (
            <div className="flex gap-3 overflow-hidden py-1">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-24 h-24 bg-gray-200 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto px-1 py-1 scrollbar-hide">
              {/* Upload Image Button */}
              <div className="flex-shrink-0">
                {uploadedImage ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        onPreviewImageSelect(-1) // Use -1 to indicate uploaded image
                      }}
                      className={`
                        flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden border-2 transition-all duration-200
                        ${isUsingUploadedImage 
                          ? 'border-primary shadow-md scale-105' 
                          : 'border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      <Image
                        src={uploadedImagePreview || uploadedImage.url}
                        image={uploadedImage} // Uploaded images don't have attribution data
                        alt="Uploaded image"
                        className="w-full h-full object-cover"
                        imageClassName="h-full"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={onRemoveUploadedImage}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex-shrink-0 w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all duration-200">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onImageUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                    {isUploading ? (
                      <div className="flex flex-col items-center gap-1">
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        <span className="text-xs text-primary">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <Upload className="h-4 w-4 text-gray-400" />
                        <span className="text-xs text-gray-500">Upload</span>
                      </div>
                    )}
                  </label>
                )}
              </div>

              {/* Preview Images */}
              {previewImages && previewImages.length > 0 ? (
                previewImages.map((image, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => onPreviewImageSelect(index)}
                    className={`
                      flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden border-2 transition-all duration-200
                      ${!isUsingUploadedImage && selectedImageIndex === index 
                        ? 'border-primary shadow-md scale-105' 
                        : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <Image
                      src={image.thumbnailUrl || image.url}
                      image={createImageEntity(image)}
                      alt={image.description || image.name || 'Preview image'}
                      className="w-full h-full object-cover"
                      imageClassName="h-full"
                    />
                  </button>
                ))
              ) : !uploadedImage && (
                <div className="flex items-center justify-center flex-1 max-w-32 border-2 border-dashed border-gray-200 rounded-lg px-8">
                  <div className="text-center">
                    <ImageIcon className="h-4 w-4 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500">No images found</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 